// FORT-362: Business Development CRM — organisations and partnerships that generate future
// members. Distinct from the BizDev *allocations* on the rota (Rota.tsx), which assign a
// staff member a focus area for a day; this is the standing record of the organisation.

export type BdOrganisationStatus = "New" | "Contacted" | "Interested" | "Cold" | "Won";

export const BD_STATUSES: BdOrganisationStatus[] = [
  "New",
  "Contacted",
  "Interested",
  "Cold",
  "Won",
];

/** Someone has engaged and it hasn't gone cold or closed — the KPI row's "active". */
export const BD_ACTIVE_STATUSES: BdOrganisationStatus[] = ["Contacted", "Interested"];

/**
 * The categories offered in the filter and the edit panel. A fixed list rather than client
 * config: these are the kinds of organisation a club works with, not a per-tenant setting,
 * and the client supplied them directly.
 *
 * FORT-374: "Other" last, deliberately — it is the fallback for an organisation that fits
 * none of the named kinds, and a catch-all listed among the real options invites itself to
 * be picked over the specific one. There is no companion free-text field: the column is
 * VARCHAR(50) with no constraint, so anything more specific belongs in `notes`.
 */
export const BD_CATEGORIES = [
  "University",
  "Corporate",
  "Residential",
  "Estate Agent",
  "Healthcare",
  "Community",
  "Local Partnership",
  "Other",
];

export interface BdOrganisation {
  id: number;
  name: string;
  category: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  status: BdOrganisationStatus;
  assignedStaffId: number | null;
  assignedStaffName: string | null;
  /** DateReviver may hand these back as Dates; treat them as either. */
  lastContactedAt: string | Date | null;
  followUpDate: string | Date | null;
  notes: string | null;
  /** FORT-367: set once the follow-up has been signed off as done. Null until then. */
  signedOffAt: string | Date | null;
  signedOffByStaffId: number | null;
  signedOffByStaffName: string | null;
  /**
   * FORT-368: who added the organisation, stamped server-side on create. The Mine filter
   * matches this OR the assignee — the client asked for "what they personally created",
   * and someone who created a record then handed it on should still see it under Mine.
   * Null on rows created before the column existed and never assigned to anyone.
   */
  createdByStaffId: number | null;
}

/**
 * The list response: the organisations plus the club's headline counts. The counts come
 * from SQL over the whole tenant set, so they stay true if the row list is ever capped —
 * deriving them from `organisations.length` would quietly understate every one.
 */
export interface BdOrganisations {
  totalCount: number;
  dueCount: number;
  activeCount: number;
  wonCount: number;
  organisations: BdOrganisation[];
}

/** Create/update payload. `id` is null when creating. */
export interface SaveBdOrganisationRequest {
  id: number | null;
  name: string;
  category: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  status: BdOrganisationStatus;
  assignedStaffId: number | null;
  lastContactedAt: string | null;
  followUpDate: string | null;
  notes: string | null;
}
